import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useT } from "../i18n/I18nProvider";
import { useAnnounce } from "../ui/Announcer";
import { Button } from "../ui/Button";
import { TextField } from "../ui/Field";
import { useFormErrors, type FormErrors } from "../ui/form";
import { Modal } from "../ui/Modal";
import { useApi } from "./services";

export const MIN_PASSWORD = 12;
const EMPTY = { current: "", next: "", repeat: "" };

/** Change your own password. The server signs every other device out. */
export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useT();
  const api = useApi();
  const announce = useAnnounce();
  const toErrors = useFormErrors();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({ fields: {} });
  const change = useMutation({ mutationFn: api.auth.changePassword });
  const set = (key: keyof typeof EMPTY) => (event: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: event.target.value }));

  function close(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setValues(EMPTY);
      setErrors({ fields: {} });
      change.reset();
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const fields: Record<string, string> = {};
    if (!values.current) fields.current_password = t("validation.required");
    if (values.next.length < MIN_PASSWORD) fields.new_password = t("account.passwordShort", { count: MIN_PASSWORD });
    else if (values.repeat !== values.next) fields.repeat = t("account.passwordMismatch");
    setErrors({ fields });
    if (Object.keys(fields).length) return;
    try {
      await change.mutateAsync({ current_password: values.current, new_password: values.next });
      announce(t("account.passwordChanged"));
      close(false);
    } catch (error) {
      setErrors(toErrors(error, { codeFields: { wrong_password: "current_password", password_unchanged: "new_password" } }));
    }
  }

  return (
    <Modal open={open} onOpenChange={close} title={t("account.changePassword")} description={t("account.changePasswordBody")}>
      <form className="cc-form" onSubmit={(event) => void submit(event)} noValidate>
        {errors.form ? <p className="cc-alert" role="alert">{errors.form}</p> : null}
        <TextField label={t("account.currentPassword")} type="password" autoComplete="current-password" value={values.current} onChange={set("current")} error={errors.fields.current_password} required />
        <TextField label={t("account.newPassword")} type="password" autoComplete="new-password" value={values.next} onChange={set("next")} error={errors.fields.new_password} hint={t("account.passwordHint", { count: MIN_PASSWORD })} required />
        <TextField label={t("account.confirmPassword")} type="password" autoComplete="new-password" value={values.repeat} onChange={set("repeat")} error={errors.fields.repeat} required />
        <div className="cc-form__actions">
          <Button onClick={() => close(false)}>{t("common.cancel")}</Button>
          <Button type="submit" variant="primary" busy={change.isPending}>
            {t("account.savePassword")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
